import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail, Plus, Sparkles, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from '@command-center/ui-system';
import { ApiClientError } from '../lib/api';
import { emailsApi, type Recipient } from '../lib/emails-api';

const labelClass = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';

/**
 * The one outbound-email surface.
 *
 * Two modes, because the AI draft has to be built from something:
 *
 *   Lead mode (`leadId` given) — POST /emails/generate writes a subject and
 *   body centred on the lead's next step, and Regenerate is offered.
 *
 *   Compose mode (no lead) — contacts, organizations, calls, and form entries
 *   have no next step for a model to aim at, so the dialog opens blank with the
 *   recipient prefilled. It still sends through the same /emails/send route,
 *   which takes an optional leadId.
 *
 * Reviewing the draft here IS the approval; there is no separate queue.
 *
 * Supports both trigger mode and controlled mode. Controlled mode exists so one
 * dialog can hand off to this one — the Lead quick view closes itself and opens
 * this in its place rather than stacking two dialogs.
 */
export function SendEmailDialog({
  leadId,
  to: prefilled,
  contextLabel,
  trigger,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: {
  leadId?: string;
  /** Recipients to start with. The only source of recipients in compose mode. */
  to?: Recipient[];
  /** Who or what this email is about, shown in the dialog description. */
  contextLabel?: string;
  trigger?: ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : uncontrolledOpen;
  const setOpen = controlled ? (setControlledOpen ?? (() => {})) : setUncontrolledOpen;

  const [from, setFrom] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [suggested, setSuggested] = useState<Recipient[]>([]);
  const [manual, setManual] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [questions, setQuestions] = useState<string[]>([]);
  const [genError, setGenError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const queryClient = useQueryClient();

  const prefilledKey = (prefilled ?? []).map((r) => r.email).join('|');

  const generate = useCallback(async () => {
    if (!leadId) return;
    setGenerating(true);
    setGenError(null);
    try {
      const draft = await emailsApi.generate(leadId);
      setFrom(draft.from);
      setSubject(draft.subject);
      setBody(draft.body);
      setSuggested(draft.suggestedRecipients);
      const nameMap: Record<string, string> = {};
      draft.suggestedRecipients.forEach((r) => {
        if (r.name) nameMap[r.email] = r.name;
      });
      setNames(nameMap);
      setSelected(draft.suggestedRecipients[0] ? [draft.suggestedRecipients[0].email] : []);
    } catch (err) {
      setGenError(
        err instanceof ApiClientError && err.code === 'openai_not_configured'
          ? "The AI draft isn't available yet — the OpenAI key still needs to be set on the Worker. You can still write the email manually."
          : err instanceof Error
            ? err.message
            : 'Draft failed.',
      );
    } finally {
      setGenerating(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (!open) {
      setSubject('');
      setBody('');
      setQuestions([]);
      setSelected([]);
      setSuggested([]);
      setManual('');
      setGenError(null);
      return;
    }
    if (leadId) {
      void generate();
      return;
    }
    // Compose mode: seed the recipient and let the user write.
    const seeds = prefilled ?? [];
    setSelected(seeds.map((r) => r.email.trim().toLowerCase()).filter(Boolean));
    setNames(
      Object.fromEntries(
        seeds.filter((r) => r.name).map((r) => [r.email.trim().toLowerCase(), r.name!]),
      ),
    );
    setFrom('');
  }, [open, leadId, generate, prefilledKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const addRecipient = (email: string, name?: string) => {
    const e = email.trim().toLowerCase();
    if (!e || selected.includes(e)) return;
    setSelected((cur) => [...cur, e]);
    if (name) setNames((cur) => ({ ...cur, [e]: name }));
  };
  const removeRecipient = (email: string) => setSelected((cur) => cur.filter((e) => e !== email));

  const send = useMutation({
    mutationFn: () =>
      emailsApi.send({
        leadId,
        to: selected,
        subject,
        body,
        questions: questions.filter((q) => q.trim()),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      if (leadId) queryClient.invalidateQueries({ queryKey: ['activity', leadId] });
      setOpen(false);
    },
  });

  const sendError = send.error;
  const sendMessage =
    sendError instanceof ApiClientError && sendError.code === 'mailgun_not_configured'
      ? 'Email sending is not configured yet — the Mailgun key still needs to be added on the Worker.'
      : sendError instanceof Error
        ? sendError.message
        : null;

  const openSuggestions = suggested.filter((r) => !selected.includes(r.email));

  const content = (
    <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {leadId ? (
            <>
              <Sparkles className="h-4 w-4 text-primary" aria-hidden /> Send Update — AI Draft
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 text-primary" aria-hidden /> Send Email
            </>
          )}
        </DialogTitle>
        <DialogDescription>
          {leadId
            ? 'Review and edit before sending. The draft is built from this lead, focused on the next step.'
            : `Write and send an email${contextLabel ? ` to ${contextLabel}` : ''}. It sends from the Cascade system and is logged under Emails.`}
        </DialogDescription>
      </DialogHeader>

      {generating ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Drafting…
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {from && (
            <div>
              <span className={labelClass}>From</span>
              <p className="mt-1 text-sm text-foreground">{from}</p>
            </div>
          )}

          <div>
            <span className={labelClass}>To</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {selected.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[12px] text-foreground"
                >
                  {names[email] ? `${names[email]} · ${email}` : email}
                  <button type="button" onClick={() => removeRecipient(email)} aria-label={`Remove ${email}`}>
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" aria-hidden />
                  </button>
                </span>
              ))}
              {selected.length === 0 && (
                <span className="text-[12px] text-muted-foreground">No recipients yet.</span>
              )}
            </div>
            {openSuggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {openSuggestions.map((r) => (
                  <button
                    key={r.email}
                    type="button"
                    onClick={() => addRecipient(r.email, r.name)}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-muted"
                  >
                    <Plus className="h-3 w-3" aria-hidden />
                    {r.name ? `${r.name} · ${r.email}` : r.email}
                  </button>
                ))}
              </div>
            )}
            <form
              className="mt-2 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                addRecipient(manual);
                setManual('');
              }}
            >
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="Add another email…"
                className="h-8"
              />
              <Button type="submit" variant="secondary" size="sm" disabled={!manual.trim()}>
                Add
              </Button>
            </form>
          </div>

          <label className="flex flex-col gap-1">
            <span className={labelClass}>Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className={labelClass}>Body</span>
            <textarea
              className="min-h-[200px] w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          <div>
            <div className="flex items-center justify-between">
              <span className={labelClass}>Questions for the recipient</span>
              <button
                type="button"
                onClick={() => setQuestions((q) => [...q, ''])}
                className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
              >
                <Plus className="h-3 w-3" aria-hidden /> Add question
              </button>
            </div>
            {questions.length === 0 ? (
              <p className="mt-1 text-[12px] italic text-muted-foreground">
                No questions. Any you add appear in a section at the end of the email.
              </p>
            ) : (
              <div className="mt-1.5 flex flex-col gap-1.5">
                {questions.map((q, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={q}
                      onChange={(e) =>
                        setQuestions((cur) => cur.map((v, idx) => (idx === i ? e.target.value : v)))
                      }
                      placeholder={`Question ${i + 1}`}
                      className="h-8"
                    />
                    <button
                      type="button"
                      onClick={() => setQuestions((cur) => cur.filter((_, idx) => idx !== i))}
                      aria-label="Remove question"
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {genError && <p className="text-[12px] text-muted-foreground">{genError}</p>}
          {sendMessage && <p className="text-sm text-destructive">{sendMessage}</p>}
        </div>
      )}

      <DialogFooter className="flex-row items-center justify-between gap-2">
        {/* Regenerate only exists in lead mode — there is nothing to regenerate from otherwise. */}
        {leadId ? (
          <button
            type="button"
            onClick={() => void generate()}
            disabled={generating}
            className="inline-flex items-center gap-1 text-[12px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden /> Regenerate
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={send.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={send.isPending || generating || selected.length === 0 || !subject.trim() || !body.trim()}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );

  // Controlled callers own the open state and render no trigger of their own.
  if (controlled) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {content}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" className="gap-1.5">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            AI draft email
          </Button>
        )}
      </DialogTrigger>
      {content}
    </Dialog>
  );
}

/**
 * An email address rendered as a link that opens the compose dialog rather than
 * the OS mail app. Cascade sends mail itself now, so handing the user off to
 * Apple Mail loses the send record and the activity log entry.
 */
export function EmailLink({
  email,
  name,
  leadId,
  className,
  children,
}: {
  email?: string | null;
  name?: string;
  /** When the address belongs to a lead, the dialog opens with the AI draft. */
  leadId?: string;
  className?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (!email) return null;
  return (
    <>
      <SendEmailDialog
        leadId={leadId}
        to={[{ email, name }]}
        contextLabel={name ?? email}
        open={open}
        onOpenChange={setOpen}
      />
      <button
        type="button"
        onClick={(event) => {
          // Email addresses sit inside clickable cards and rows; opening the
          // composer must not also open the record behind it.
          event.stopPropagation();
          setOpen(true);
        }}
        className={className ?? 'truncate text-primary hover:underline'}
        title={`Email ${email}`}
      >
        {children ?? email}
      </button>
    </>
  );
}
